import { useState, useEffect } from "react";
import {
	DataTable,
	DataTableExpandedRows,
	DataTableRowEvent,
	DataTableValueArray,
} from "primereact/datatable";
import { Column } from "primereact/column";
import { Loading } from "../../../components/Loading";
import { CustomSortIcon } from "../../../components/DatatableHelpers";
import Select from "../../../components/input/Select";
import SimplePagination from "../../../components/SimplePagination";
import FormattedDate from "../../../components/FormattedDate";
import { SelectListItem } from "../../../components/input/SelectList";
import { SimpleOption } from "../../../components/input/SimpleListbox";
import { PeerReviewsActivityPerPromptViewModel } from "../peerReviewsApiSlice";
import { ReviewRequestExpansionRow, TableView } from "./ReviewRequestExpansionRow";
import { progressBodyTemplate, totalReviewsBodyTemplate } from "./PeerReviewActivityUtil";

interface PeerReviewStatusProps {
	data?: PeerReviewsActivityPerPromptViewModel[];
	writerPool?: SelectListItem[];
	selectedPromptId?: number;
}

const getPromptNameById = (
	promptId: number | undefined,
	dataList?: PeerReviewsActivityPerPromptViewModel[]
) => {
	if (!promptId || !dataList) return "All";
	const prompt = dataList.find((item) => item.promptId === promptId);
	return prompt?.promptName ?? "All";
};

export default function PeerReviewActivityTable({
	data,
	writerPool,
	selectedPromptId,
}: PeerReviewStatusProps) {
	// Initialization
	const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows | DataTableValueArray>();
	const [page, setPage] = useState(0);
	const [selectedPrompt, setSelectedPrompt] = useState<string>(
		getPromptNameById(selectedPromptId, data)
	);
	const [selectedWriter, setSelectedWriter] = useState<string>("All");
	const [selectedReviewer, setSelectedReviewer] = useState<string>("All");
	const [selectedView, setSelectedView] = useState<TableView>(TableView.Student);

	useEffect(() => {
		if (selectedPromptId && data) {
			setSelectedPrompt(getPromptNameById(selectedPromptId, data));
			setExpandedRows({
				[selectedPromptId]: true,
			});
		}
	}, [selectedPromptId, data]);

	// Data - Apply filters
	const filteredData = data?.filter((item) => {
		// Apply prompt filter
		if (selectedPrompt && selectedPrompt !== "All" && item.promptName !== selectedPrompt) {
			return false;
		}

		// Check if this prompt has any matching requests
		const hasMatchingRequests = item.reviewRequests.some((writer) => {
			// Writer filter
			if (
				selectedWriter &&
				selectedWriter !== "All" &&
				writer.writer.id.toString() !== selectedWriter
			) {
				return false;
			}

			// Check if writer has matching reviewer requests
			if (!writer.requests || writer.requests.length === 0) {
				// If reviewer filter is applied, writers with no requests don't match
				if (selectedReviewer && selectedReviewer !== "All") {
					return false;
				}
				// Otherwise, include if writer filter matches
				return selectedWriter === "All" || writer.writer.id.toString() === selectedWriter;
			}

			return writer.requests.some((request) => {
				// Reviewer filter
				if (
					selectedReviewer &&
					selectedReviewer !== "All" &&
					request.reviewer.id.toString() !== selectedReviewer
				) {
					return false;
				}
				return true;
			});
		});

		return hasMatchingRequests;
	});

	// Pagination
	const pageSize = 10;
	const handlePageChange = (newPage: number) => {
		setPage(newPage);
		setExpandedRows(undefined); // Close any expanded rows when changing pages
	};

	const totalCount = filteredData?.length;
	const paginatedData = filteredData?.slice(page * pageSize, (page + 1) * pageSize);

	// Filtering options
	const uniquePrompts = data
		? Array.from(new Set(data.map((item) => item.promptName).filter(Boolean))).sort()
		: [];

	const promptOptions = [
		{ value: "All", label: "All" },
		...uniquePrompts.map((prompt) => ({ value: prompt, label: prompt })),
	];

	const writerPoolOptions = [
		{ value: "All", label: "All" } as SimpleOption,
		...(writerPool?.flatMap(
			(writer) =>
				({
					value: writer.value,
					label: writer.text,
				}) as SimpleOption
		) ?? []),
	];

	// Table
	const onRowToggle = (e: DataTableRowEvent) => {
		setExpandedRows(e.data);
	};

	const onRowClick = (e: DataTableRowEvent) => {
		const clickedRow = e.data as PeerReviewsActivityPerPromptViewModel;
		const isExpanded = expandedRows && (expandedRows as any)[clickedRow.promptId];

		if (isExpanded) {
			setExpandedRows(undefined);
		} else {
			setExpandedRows({
				[clickedRow.promptId]: true,
			});
		}
	};

	const rowExpansionTemplate = (rowData: PeerReviewsActivityPerPromptViewModel) => {
		return (
			<ReviewRequestExpansionRow
				data={rowData.reviewRequests}
				writerPool={writerPoolOptions}
				selectedView={selectedView}
				setSelectedView={setSelectedView}
			/>
		);
	};

	const requestDateBodyTemplate = (rowData: PeerReviewsActivityPerPromptViewModel) => {
		return rowData.latestReviewDate ? <FormattedDate date={rowData.latestReviewDate} /> : "-";
	};

	return (
		<section className="mt-4">
			<Loading loading={!data} />
			{data && (
				<>
					<div className="mb-4 flex gap-4">
						<Select
							value={selectedPrompt}
							setValue={(value) => setSelectedPrompt(value as string)}
							simpleListBoxId="prompt-filter"
							options={promptOptions}
							label="Prompt"
							fieldClasses="w-64"
						/>
						<Select
							value={selectedWriter}
							setValue={(value) => setSelectedWriter(value as string)}
							simpleListBoxId="writer-filter"
							options={writerPoolOptions}
							label="Writer"
							fieldClasses="w-64"
						/>
						<Select
							value={selectedReviewer}
							setValue={(value) => setSelectedReviewer(value as string)}
							simpleListBoxId="reviewer-filter"
							options={writerPoolOptions}
							label="Reviewer"
							fieldClasses="w-64"
						/>
					</div>
					<DataTable
						value={paginatedData}
						dataKey="promptId"
						stripedRows
						expandedRows={expandedRows}
						onRowToggle={onRowToggle}
						onRowClick={onRowClick}
						rowExpansionTemplate={rowExpansionTemplate}
						sortIcon={CustomSortIcon}
						emptyMessage="No results found"
						className="tbl tbl-data tbl-datatable table-fixed min-w-[40rem]"
						rowClassName={() => "group cursor-pointer"}>
						<Column
							expander
							headerClassName="!pl-3 !pr-0.5"
							className="!pl-3 !pr-0.5"
						/>
						<Column header="Prompt" field="promptName" sortable />
						<Column
							header="Latest Review Date"
							field="latestReviewDate"
							body={requestDateBodyTemplate}
							sortable
							sortField="latestReviewDate"
							style={{ minWidth: "120px" }}
						/>
						<Column
							header="Total Requested Reviews"
							body={totalReviewsBodyTemplate}
							sortable
							sortField="totalReviews"
						/>
						<Column
							body={(rowData: PeerReviewsActivityPerPromptViewModel) =>
								progressBodyTemplate(rowData, rowData.promptId)
							}
							header="Status"
							style={{ minWidth: "120px" }}
						/>
					</DataTable>
					{paginatedData &&
						paginatedData.length > 0 &&
						totalCount &&
						totalCount > pageSize && (
							<SimplePagination
								itemCount={totalCount ?? 0}
								page={page}
								pageSize={pageSize}
								totalCount={totalCount}
								onChange={(page) => handlePageChange(page)}
								className="border-none"
							/>
						)}
				</>
			)}
		</section>
	);
}
